import { useState } from "react";

type Props = {
  userData: any;
  isEditing: boolean;
};

export function PersonalInfoSection({ userData }: Props) {
  const [activeSection, setActiveSection] = useState<
    "personal" | "employment" | "payroll"
  >("personal");

  if (!userData) return null;

  const sections = [
    { id: "personal", label: "Persönliche Daten" },
    { id: "employment", label: "Beschäftigung" },
    { id: "payroll", label: "Lohn & Urlaub" },
  ] as const;

  const Row = ({
    label,
    value,
    border = true,
  }: {
    label: string;
    value: React.ReactNode;
    border?: boolean;
  }) => (
    <div
      className={`grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-4 py-4 ${
        border ? "border-b border-[#e9e9e9]" : ""
      }`}
    >
      <label className="text-[#6b6b6b] text-[13px] sm:text-[14px] font-medium">
        {label}
      </label>

      <div className="sm:col-span-2">
        <p className="text-[#1c1c1c] text-[14px] break-words">{value}</p>
      </div>
    </div>
  );

  const formatDate = (date?: string) => {
    if (!date) return "-";

    const parsed = new Date(date);

    if (isNaN(parsed.getTime())) return "-";

    return new Intl.DateTimeFormat("de-DE", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(parsed);
  };

  return (
    <div className="flex flex-col md:flex-row gap-6 md:gap-8">
      {/* DESKTOP SIDEBAR */}
      <div className="hidden md:block w-[220px] border-r border-[#e9e9e9] pr-4 space-y-2">
        {sections.map((section) => (
          <button
            key={section.id}
            onClick={() => setActiveSection(section.id)}
            className={`block w-full text-left px-3 py-2 rounded-lg text-[14px] transition-colors ${
              activeSection === section.id
                ? "bg-[#0084c7]/10 text-[#0084c7]"
                : "text-[#6b6b6b] hover:bg-[#f5f5f5]"
            }`}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div className="flex-1 space-y-5">
        <h2 className="text-[#1c1c1c] text-[18px] md:text-[22px] font-medium">
          Persönliche Informationen
        </h2>

        {/* MOBILE SLIDER */}
        <div className="md:hidden -mx-2 px-2 overflow-x-auto no-scrollbar snap-x snap-mandatory">
          <div className="flex gap-2 min-w-max">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`snap-start shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-[13px] font-medium transition-colors border ${
                  activeSection === section.id
                    ? "bg-[#0084c7] text-white border-[#0084c7]"
                    : "bg-white text-[#6b6b6b] border-[#e9e9e9]"
                }`}
              >
                {section.label}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2 sm:space-y-4">
          {activeSection === "personal" && (
            <>
              <Row label="Vorname" value={userData.first_name} />
              <Row label="Nachname" value={userData.last_name} />
              <Row label="E-Mail" value={userData.email} />
              <Row label="Benutzername" value={userData.username} border={false} />
            </>
          )}

          {activeSection === "employment" && (
            <>
              <Row label="Benutzer-Typ" value={userData.user_type?.type} />
              <Row label="Beruf" value={userData.job_title} />
              <Row label="Status" value={userData.status} />
              <Row label="Eintrittsdatum" value={formatDate(userData.entry_date)} />
              <Row label="Personalnummer (Datev)" value={userData.internal_number} />
              <Row
                label="Tägliche Arbeitszeit"
                value={
                  userData.work_schedule
                    ? (() => {
                        const days: { key: keyof typeof userData.work_schedule; label: string }[] = [
                          { key: 'monday_hours',    label: 'Mo' },
                          { key: 'tuesday_hours',   label: 'Di' },
                          { key: 'wednesday_hours', label: 'Mi' },
                          { key: 'thursday_hours',  label: 'Do' },
                          { key: 'friday_hours',    label: 'Fr' },
                          { key: 'saturday_hours',  label: 'Sa' },
                          { key: 'sunday_hours',    label: 'So' },
                        ];
                        return (
                          <div className="flex flex-wrap gap-2">
                            {days.map(({ key, label }) => (
                              <div key={String(key)} className="flex flex-col items-center px-4 py-2 bg-[#f5f5f5] rounded-lg min-w-[40px]">
                                <span className="text-[11px] text-[#6b6b6b]">{label}</span>
                                <span className="text-[13px] pt-1 font-medium text-[#1c1c1c]">
                                  {userData.work_schedule[key]} Std
                                </span>
                              </div>
                            ))}
                          </div>
                        );
                      })()
                    : `${userData.daily_work_time} Std`
                }
                border={false}
              />
            </>
          )}

          {activeSection === "payroll" && (
            <>
              <Row label="Urlaubstage gesamt" value={userData.holiday_total} />
              <Row label="Offene Urlaubstage" value={userData.open_holidays} />
              <Row label="Stundenlohn" value={`${userData.hourly_wage} €`} />
              <Row
                label="Zeitguthaben"
                value={`${userData.time_credit} Std`}
                border={false}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}